/**
 * Formats YYYY-MM-DD or ISO date strings to DD-MM-YYYY, safely preserving already-formatted DD-MM-YYYY dates.
 * Also handles strings containing embedded dates (e.g. revision logs, change summaries).
 * By default, time-of-day is excluded unless includeTime is explicitly true.
 */
export function formatDateDDMMYYYY(dateStr?: string | null, includeTime: boolean = false): string {
  if (!dateStr) return '';
  const str = String(dateStr).trim();
  
  // If already in standalone DD-MM-YYYY format (with optional time)
  const ddmmyyyyMatch = str.match(/^(\d{2}-\d{2}-\d{4})(?:[T\s](\d{2}:\d{2}(?::\d{2})?))?$/);
  if (ddmmyyyyMatch) {
    const [_, datePart, timePart] = ddmmyyyyMatch;
    return includeTime && timePart ? `${datePart} ${timePart}` : datePart;
  }

  // Regex to detect standalone YYYY-MM-DD (with optional time, timezone, or milliseconds)
  const isoMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}:\d{2}(?::\d{2})?)(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?)?$/);
  if (isoMatch) {
    const [_, year, month, day, time] = isoMatch;
    const formattedDate = `${day}-${month}-${year}`;
    return includeTime && time && time !== '00:00' && time !== '00:00:00' ? `${formattedDate} ${time}` : formattedDate;
  }

  // If the string contains embedded YYYY-MM-DD dates (e.g. "Planned End Date: 2027-12-31 → 2028-06-30")
  // Replace each embedded YYYY-MM-DD with DD-MM-YYYY
  if (/\d{4}-\d{2}-\d{2}/.test(str)) {
    return str.replace(/(\d{4})-(\d{2})-(\d{2})/g, (_match, year, month, day) => {
      return `${day}-${month}-${year}`;
    });
  }
  
  // Only attempt Date object parsing if string explicitly matches standard date patterns with 4-digit year
  const looksLikeDate = /^(?:\d{4}[-/]\d{1,2}[-/]\d{1,2}|(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+\d{1,2},?\s+\d{4}|\d{1,2}\s+(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*,?\s+\d{4})/i.test(str);

  if (looksLikeDate) {
    try {
      const cleanDatePart = str.split('T')[0];
      const ymdMatch = cleanDatePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (ymdMatch && !includeTime) {
        return `${ymdMatch[3]}-${ymdMatch[2]}-${ymdMatch[1]}`;
      }

      const d = new Date(str);
      if (isNaN(d.getTime())) return str;
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      const hours = String(d.getHours()).padStart(2, '0');
      const minutes = String(d.getMinutes()).padStart(2, '0');
      const seconds = String(d.getSeconds()).padStart(2, '0');
      const hasTime = d.getHours() > 0 || d.getMinutes() > 0 || d.getSeconds() > 0;
      const formattedDate = `${day}-${month}-${year}`;
      return includeTime && hasTime ? `${formattedDate} ${hours}:${minutes}:${seconds}` : formattedDate;
    } catch {
      return str;
    }
  }

  return str;
}
